// hooks/time/useSharedTimeRangeController.ts

import { useCallback, useState } from 'react';

/**
 * useSharedTimeRangeController
 *
 * A custom React hook for managing a shared time range across multiple charts or components.
 *
 * Useful in synchronized dashboards where zooming or brushing on one chart
 * should reflect across other linked charts.
 *
 * @returns An object with:
 * - `sharedTimeRange`: the current shared time range as a [startDate, endDate] tuple, or `null` if not set
 * - `setSharedTimeRange`: direct setter for updating the shared time range
 * - `resetTimeRange()`: clears the shared time range (sets it to `null`)
 * - `handleRangeChange(range)`: handler function to update the shared range externally
 */
export function useSharedTimeRangeController() {
  const [sharedTimeRange, setSharedTimeRange] = useState<[Date, Date] | null>(null);

  /**
   * Clears the current shared time range.
   */
  const resetTimeRange = useCallback(() => setSharedTimeRange(null), []);

  /**
   * Sets a new shared time range or clears it if `null` is passed.
   *
   * @param range - A tuple of [startDate, endDate] or `null` to reset
   */
  const handleRangeChange = useCallback((range: [Date, Date] | null) => {
    setSharedTimeRange(range);
  }, []);

  return {
    sharedTimeRange,
    setSharedTimeRange,
    resetTimeRange,
    handleRangeChange,
  };
}
